import {
  PMS_APPOINTMENTS,
  PMS_COMM_LOG,
  PMS_TRANSACTIONS,
  PMS_TREATMENT_PLANS,
  RECORD_ACTIVITIES,
} from 'app/types/pms-record'
import type { PmsRecord, PmsRecordGroup, PmsRecordKind, PmsRecordLike, PmsRow } from 'app/types/pms-record'

export {
  PMS_APPOINTMENTS,
  PMS_COMM_LOG,
  PMS_RECORD_KINDS,
  PMS_TRANSACTIONS,
  PMS_TREATMENT_PLANS,
  RECORD_ACTIVITIES,
} from 'app/types/pms-record'

export type { PmsRecord, PmsRecordGroup, PmsRecordKind, PmsRecordLike, PmsRow } from 'app/types/pms-record'

/**
 * Merging the PMS record types into one chronological stream.
 *
 * Appointments, transactions, treatment plans and comm logs each carry their
 * own date field and their own card, but they answer one question together:
 * what happened with this patient. A broken appointment next to the text where
 * the patient cancelled it is the story staff actually want, and no
 * single-select tab can show it.
 */

/**
 * The comm log is off by default. It is the largest and least deliberate of the
 * four (on a sampled patient, 88 of 95 records were automated reminder
 * traffic), so it opens as an opt-in layer rather than the loudest voice in a
 * stream you did not ask for.
 */
export const DEFAULT_PMS_KINDS: PmsRecordKind[] = [PMS_APPOINTMENTS, PMS_TRANSACTIONS, PMS_TREATMENT_PLANS]

/** Undated records sink to the bottom instead of jumping to the top as epoch 0. */
const sortKey = (record: PmsRecordLike) => (record.date ? new Date(record.date).getTime() : Number.NEGATIVE_INFINITY)

export function sortPmsRecordsDesc<R extends PmsRecordLike>(records: R[]): R[] {
  return [...records].sort((a, b) => sortKey(b) - sortKey(a))
}

/**
 * Builds the ordered month groups for the selected record kinds. `groupLabel`
 * is injected so this stays free of date-formatting concerns and the caller can
 * keep using the same month labels the rest of the feed uses.
 */
export function groupPmsRecords<R extends PmsRecordLike>({
  records,
  selected,
  groupLabel,
}: {
  records: R[]
  selected: PmsRecordKind[]
  groupLabel: (date?: string | null) => string
}): PmsRecordGroup<R>[] {
  const kinds = new Set<string>(selected)
  const sorted = sortPmsRecordsDesc(records.filter((record) => kinds.has(record.kind)))

  const groups: PmsRecordGroup<R>[] = []
  for (const record of sorted) {
    const label = groupLabel(record.date)
    const current = groups[groups.length - 1]
    // Records arrive sorted, so a run of the same label is always contiguous
    // and only the last group needs checking.
    if (current?.label === label) current.records.push(record)
    else groups.push({ label, records: [record] })
  }

  return groups
}

/**
 * Folds runs of collapsible records inside one month group.
 *
 * Only a contiguous run collapses, which is the point: once an appointment or a
 * payment lands between two automated messages, the run is genuinely broken and
 * the timeline should say so. `isCollapsible` is injected so this module stays
 * ignorant of what makes a comm log automated.
 */
export function collapseRuns<R extends PmsRecordLike>({
  records,
  keyFor,
  minRunLength,
}: {
  records: R[]
  /**
   * What makes two neighbours the same thing. Records sharing a key collapse
   * together; `undefined` means the record never collapses. Automated comm logs
   * return one shared key, a ledger returns the transaction type.
   */
  keyFor: (record: R) => string | undefined
  minRunLength: number
}): PmsRow<R>[] {
  const rows: PmsRow<R>[] = []
  let run: R[] = []
  let runKey: string | undefined

  const flush = () => {
    if (run.length === 0) return
    if (run.length < minRunLength) for (const record of run) rows.push({ kind: `record`, record })
    else rows.push({ kind: `run`, records: run })
    run = []
    runKey = undefined
  }

  for (const record of records) {
    const key = keyFor(record)
    if (key === undefined) {
      flush()
      rows.push({ kind: `record`, record })
      continue
    }
    if (key !== runKey) flush()
    runKey = key
    run.push(record)
  }
  flush()

  return rows
}

/**
 * Folds every matching record in a group into one row, wherever the first of
 * them sits.
 *
 * Different from `collapseRuns` on purpose. A run collapses neighbours, which
 * suits the comm log and the ledger because their repetition is contiguous.
 * Contact updates are not: a month holds a status change, then a call, then two
 * more status changes, so a run-based fold would leave three cards saying the
 * same kind of thing. This gathers them all into one and keeps it in the
 * position of the newest, so the group's chronology still reads correctly
 * around it.
 *
 * A lone match folds too. One update in a month is still an update, and leaving
 * it on the ungrouped card meant the same record read two different ways
 * depending on how many neighbours it happened to have that month.
 */
export function foldMatchingRows<R extends PmsRecordLike>({
  rows,
  keyFor,
}: {
  rows: PmsRow<R>[]
  keyFor: (record: R) => string | undefined
}): PmsRow<R>[] {
  const matches = new Map<string, R[]>()
  for (const row of rows) {
    if (row.kind !== `record`) continue
    const key = keyFor(row.record)
    if (key === undefined) continue
    matches.set(key, [...(matches.get(key) ?? []), row.record])
  }

  if (matches.size === 0) return rows

  const used = new Set<string>()
  const folded: PmsRow<R>[] = []
  for (const row of rows) {
    if (row.kind !== `record`) {
      folded.push(row)
      continue
    }
    const key = keyFor(row.record)
    if (key === undefined) {
      folded.push(row)
      continue
    }
    if (used.has(key)) continue
    used.add(key)
    folded.push({ kind: `run`, records: matches.get(key)! })
  }

  return folded
}
